import type { DataTypeDto, PendingDecision, PendingValueDto, PendingValuesDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText } from '../../ui';

/** I valori sospesi aperti di un tipo, o di una sola voce (ADR 0211 passo 3, dall'ADR 0198). */
export function usePendingValues(tipo: string, rif?: string) {
  const { isManager } = useTenant();
  const query = new URLSearchParams({ tipo, ...(rif ? { rif } : {}) }).toString();
  return useTenantQuery<PendingValuesDto>(['model', 'pending', tipo, rif ?? ''], `/model/pending?${query}`, { enabled: isManager });
}

/**
 * I valori da sistemare: arrivati fuori formato (da correggere, non registrati) o leggibili in più modi (da confermare,
 * registrati nella lettura più probabile). Per ognuno il valore com'è arrivato, il motivo e da dove viene; si corregge,
 * si conferma una lettura (o il valore com'è, se il formato non ha un controllo matematico) o si scarta.
 */
export function PendingValues({ type, rif }: { type: DataTypeDto; rif?: string }) {
  const q = usePendingValues(type.chiave, rif);
  const list = q.data?.sospesi ?? [];
  if (!list.length) return null;
  const label = (a: string) => type.attributi.find((x) => x.nome === a)?.etichetta ?? a.replace(/_/g, ' ');
  return (
    <Card>
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Valori da sistemare <Badge tone="amber">{list.length}</Badge>
      </h2>
      <p className="mt-1 text-xs text-slate-500">Arrivati da documenti, posta o programmi senza rispettare il formato del campo, o leggibili in più modi. Non si perdono: decidi tu.</p>
      <ul className="mt-3 divide-y divide-slate-100">
        {list.map((p) => (
          <PendingRow key={p.id} p={p} label={label(p.attributo)} showName={!rif} />
        ))}
      </ul>
    </Card>
  );
}

function PendingRow({ p, label, showName }: { p: PendingValueDto; label: string; showName: boolean }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decide = async (body: PendingDecision) => {
    setBusy(true);
    setError(null);
    try {
      await api(t(`/model/pending/${p.id}`), { body });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model', 'pending'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model'] }),
      ]);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  const last = p.fonti[p.fonti.length - 1];
  const others = p.esito === 'da_confermare' ? p.letture.slice(1) : [];
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        {showName && <span className="font-medium">{p.nome ?? p.rif}</span>}
        <span className="text-slate-500">{label}</span>
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{p.grezzo}</code>
        <Badge tone={p.esito === 'da_correggere' ? 'red' : 'amber'}>{p.esito === 'da_correggere' ? 'Da correggere' : 'Da confermare'}</Badge>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {p.motivo.charAt(0).toUpperCase() + p.motivo.slice(1)}
        {p.esito === 'da_confermare' && p.valore_attuale !== null ? ` · registrato come «${String(p.valore_attuale)}»` : ''}
        {last ? ` · da ${last.fonte}${p.fonti.length > 1 ? ` e altre ${p.fonti.length - 1}` : ''}` : ''}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {p.esito === 'da_confermare' && (
          <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'confermato' })}>
            Conferma «{String(p.valore_attuale ?? p.letture[0] ?? p.grezzo)}»
          </Button>
        )}
        {others.map((x) => (
          <Button key={String(x)} variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'confermato', valore: String(x) })}>
            È «{String(x)}»
          </Button>
        ))}
        {p.esito === 'da_correggere' && p.confermabile && (
          <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'confermato' })}>
            Va bene così
          </Button>
        )}
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) void decide({ decisione: 'corretto', valore: value.trim() });
          }}
        >
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Valore giusto"
            aria-label={`Valore giusto per ${label}`}
            className="w-48 rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
          <Button type="submit" disabled={busy || !value.trim()}>
            Correggi
          </Button>
        </form>
        <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'scartato' })}>
          Scarta
        </Button>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </li>
  );
}
